import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import useCurrentUserAccess from "../../hooks/useCurrentUserAccess";
import API_BASE_URL from "../../config";
import { getHostContext } from "../../utils/hostContext";

const NAVBAR_LOGO_CACHE_KEY = "agentbook_navbar_logo_url";

function Navbar() {
  const { loading, platformRole, accountRole } = useCurrentUserAccess();

  const [navbarLogoUrl, setNavbarLogoUrl] = useState(() => {
    return localStorage.getItem(NAVBAR_LOGO_CACHE_KEY) || "";
  });

  const [logoLoaded, setLogoLoaded] = useState(
    Boolean(localStorage.getItem(NAVBAR_LOGO_CACHE_KEY))
  );
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [featureKeys, setFeatureKeys] = useState([]);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);

  const location = useLocation();

  const { isTenantSubdomain } = getHostContext();
  const isDemoSubdomain = isTenantSubdomain;

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isPlatformEmployee = platformRole?.key_name === "platform_employee";
  const isAccountAdmin = accountRole?.key_name === "account_admin";
  const isAccountUser = accountRole?.key_name === "account_user";

  const isPlatformSide = !isDemoSubdomain && (isPlatformAdmin || isPlatformEmployee);
  const isAccountSide = isDemoSubdomain || isAccountAdmin || isAccountUser;

  const hasFeature = (featureKey) => {
    if (isPlatformSide) return true;
    return featureKeys.includes(featureKey);
  };

  useEffect(() => {
    let isMounted = true;

    const updateLogo = (logoUrl) => {
      const cleanLogoUrl = String(logoUrl || "").trim();

      if (!isMounted) return;

      setNavbarLogoUrl(cleanLogoUrl);
      setLogoLoaded(true);

      if (cleanLogoUrl) {
        localStorage.setItem(NAVBAR_LOGO_CACHE_KEY, cleanLogoUrl);
      } else {
        localStorage.removeItem(NAVBAR_LOGO_CACHE_KEY);
      }
    };

    async function loadNavbarLogo() {
      try {
        const token = localStorage.getItem("token");

        if (isAccountSide && token) {
          const accountRes = await fetch(`${API_BASE_URL}/api/settings/account-appearance`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (accountRes.ok) {
            const accountData = await accountRes.json();
            const accountLogo =
              accountData?.navbar_logo_url ||
              accountData?.login_logo_url ||
              "";

            if (accountLogo) {
              updateLogo(accountLogo);
              return;
            }
          }
        }

        const platformRes = await fetch(`${API_BASE_URL}/api/platform-settings`);
        const platformData = await platformRes.json();

        updateLogo(platformData?.navbar_logo_url || "");
      } catch (err) {
        console.error("Failed to load navbar logo:", err);

        if (isMounted) {
          setLogoLoaded(true);
        }
      }
    }

    if (!loading) {
      loadNavbarLogo();
    }

    return () => {
      isMounted = false;
    };
  }, [loading, isAccountSide]);

  useEffect(() => {
    let isMounted = true;

    async function loadCurrentPlanFeatures() {
      try {
        setFeaturesLoaded(false);

        const token = localStorage.getItem("token");

        if (!token) {
          if (isMounted) {
            setFeatureKeys([]);
            setFeaturesLoaded(true);
          }
          return;
        }

        if (isPlatformSide) {
          if (isMounted) {
            setFeatureKeys([]);
            setFeaturesLoaded(true);
          }
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/subscription-plans/my-features`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load subscription features");
        }

        if (isMounted) {
          setFeatureKeys(Array.isArray(data.feature_keys) ? data.feature_keys : []);
          setFeaturesLoaded(true);
        }
      } catch (err) {
        console.error("Failed to load subscription features:", err.message);

        if (isMounted) {
          setFeatureKeys([]);
          setFeaturesLoaded(true);
        }
      }
    }

    if (!loading) {
      loadCurrentPlanFeatures();
    }

    return () => {
      isMounted = false;
    };
  }, [loading, isPlatformSide]);

  useEffect(() => {
    const handleToggleMobileSidebar = () => {
      setIsMobileSidebarOpen((prev) => !prev);
    };

    const handleCloseMobileSidebar = () => {
      setIsMobileSidebarOpen(false);
    };

    window.addEventListener("toggle-mobile-sidebar", handleToggleMobileSidebar);
    window.addEventListener("close-mobile-sidebar", handleCloseMobileSidebar);

    return () => {
      window.removeEventListener("toggle-mobile-sidebar", handleToggleMobileSidebar);
      window.removeEventListener("close-mobile-sidebar", handleCloseMobileSidebar);
    };
  }, []);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const sidebarClassName = `sidebar ${isMobileSidebarOpen ? "sidebar-open" : ""}`;

  const renderLogo = () => (
    <div className="logo">
      {navbarLogoUrl ? (
        <img src={navbarLogoUrl} alt="Agent Book CRM Logo" />
      ) : logoLoaded ? (
        <div className="navbar-logo-placeholder">Agent Book CRM</div>
      ) : (
        <div className="navbar-logo-placeholder" />
      )}
    </div>
  );

  if (loading || (!isPlatformSide && isAccountSide && !featuresLoaded)) {
    return (
      <>
        {isMobileSidebarOpen && (
          <button
            type="button"
            className="sidebar-backdrop"
            aria-label="Close navigation menu"
            onClick={closeMobileSidebar}
          />
        )}

        <div className={sidebarClassName}>
          {renderLogo()}
        </div>
      </>
    );
  }

  return (
    <>
      {isMobileSidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close navigation menu"
          onClick={closeMobileSidebar}
        />
      )}

      <div className={sidebarClassName}>
        {renderLogo()}

        <Link to="/dashboard" className="nav-link" onClick={closeMobileSidebar}>
          Dashboard
        </Link>

        {isPlatformSide && (
          <>
            <Link to="/sales-leads" className="nav-link" onClick={closeMobileSidebar}>
              Leads
            </Link>
            <Link to="/subscribers" className="nav-link" onClick={closeMobileSidebar}>
              Subscribers
            </Link>
            <Link to="/demo-accounts" className="nav-link" onClick={closeMobileSidebar}>
              Demo Accounts
            </Link>
            <Link to="/opportunities" className="nav-link" onClick={closeMobileSidebar}>
              Opportunities
            </Link>
            {isPlatformAdmin && (
              <Link to="/users" className="nav-link" onClick={closeMobileSidebar}>
                Users
              </Link>
            )}
            <Link to="/calendar" className="nav-link" onClick={closeMobileSidebar}>
              Calendar
            </Link>
            <Link to="/templates" className="nav-link" onClick={closeMobileSidebar}>
              Templates
            </Link>
            <Link to="/automations" className="nav-link" onClick={closeMobileSidebar}>
              Workflows
            </Link>
            <Link to="/reports" className="nav-link" onClick={closeMobileSidebar}>
              Reports
            </Link>
            <Link to="/settings" className="nav-link" onClick={closeMobileSidebar}>
              Settings
            </Link>
            <Link to="/support" className="nav-link" onClick={closeMobileSidebar}>
              Help / Support
            </Link>
          </>
        )}

        {isAccountSide && (
          <>
            <Link to="/leads" className="nav-link" onClick={closeMobileSidebar}>
              Leads
            </Link>

            <Link to="/clients" className="nav-link" onClick={closeMobileSidebar}>
              Clients
            </Link>

            <Link to="/opportunities" className="nav-link" onClick={closeMobileSidebar}>
              Opportunities
            </Link>

            <Link to="/policies" className="nav-link" onClick={closeMobileSidebar}>
              Policies
            </Link>

            {hasFeature("team_users") && (
              <Link to="/users" className="nav-link" onClick={closeMobileSidebar}>
                Users
              </Link>
            )}

            <Link to="/calendar" className="nav-link" onClick={closeMobileSidebar}>
              Calendar
            </Link>

            <Link to="/templates" className="nav-link" onClick={closeMobileSidebar}>
              Templates
            </Link>

            {hasFeature("automated_workflows") && (
              <Link to="/automations" className="nav-link" onClick={closeMobileSidebar}>
                Workflows
              </Link>
            )}

            {hasFeature("reporting") && (
              <Link to="/reports" className="nav-link" onClick={closeMobileSidebar}>
                Reports
              </Link>
            )}

            <Link to="/settings" className="nav-link" onClick={closeMobileSidebar}>
              Settings
            </Link>

            <Link to="/support" className="nav-link" onClick={closeMobileSidebar}>
              Help / Support
            </Link>
          </>
        )}

        {!isPlatformSide && !isAccountSide && (
          <>
            <Link to="/reports" className="nav-link" onClick={closeMobileSidebar}>
              Reports
            </Link>
            <Link to="/settings" className="nav-link" onClick={closeMobileSidebar}>
              Settings
            </Link>
            <Link to="/support" className="nav-link" onClick={closeMobileSidebar}>
              Help / Support
            </Link>
          </>
        )}
      </div>
    </>
  );
}

export default Navbar;